import DN from "owp.dn";
import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { classNames } from "@gss/common/classNames";
import type { TreeNode } from "@gss/common/OrgTree";
import { OrgTree } from "@gss/common/OrgTree";
import { sweCompare } from "@gss/common/sweCompare";
import { BadgeLength } from "@gss/components/BadgeLength";
import { Button } from "@gss/components/Button";
import { Col } from "@gss/components/Col";
import {
    Check,
    Collapse,
    Expand,
    Placeholder,
    Remove,
} from "@gss/components/icons";
import { InputCheckboxLabel } from "@gss/components/InputCheckboxLabel";
import { useMessages } from "@gss/components/Messages";
import { Row } from "@gss/components/Row";
import type { FloraOrg } from "@gss/types/Flora";
import type {
    MeasurementStat,
    OrgStructure,
    OrgStructureOrg,
} from "@gss/types/measurementStat";
import type { StringRecord } from "@gss/types/StringRecord";
import { DAO } from "../DAO";
import type { UserOrganisations } from "../types";
import "./DownloadStandardReport.css";
import { useTranslation } from "../util/Translation";

interface Props {
    stat: MeasurementStat;
    userOrganistions: UserOrganisations;
    filterParams: string[];
}

interface Organisation {
    type: "country" | "healthcareRegion" | "region" | "unit";
    name: string;
    id: string;
    dn: string;
    prioritize: boolean;
    children: Organisation[];
}

export function DownloadStandardReport({
    stat,
    userOrganistions,
    filterParams,
}: Props): JSX.Element | null {
    const [opened, setOpened] = useState<Organisation>();
    const [selected, setSelected] = useState<Organisation[]>([]);
    const [currentLevel, setCurrentLevel] = useState(100);
    const [country, setCountry] = useState<Organisation>();
    const [includeHistory, setIncludeHistory] = useState(false);
    const [includeQuestions, setIncludeQuestions] = useState(true);
    const [showOrganisations, setShowOrganisations] = useState(false);
    const { translation } = useTranslation();
    const { addError } = useMessages();
    const t = translation.download;

    const fetch = async (
        orgStructure: OrgStructure,
        includeHealthcareRegion: boolean,
    ): Promise<Organisation> => {
        const orgTree = new OrgTree(
            orgStructure.organisations,
            (o) => o.dn,
            (o) => o,
        );

        let children = getRegionsFromTree(orgTree, userOrganistions);

        if (includeHealthcareRegion) {
            const [healthcareRegions, regionsToHealthcareRegions] =
                await Promise.all([
                    DAO.flora.getHealthcareRegions(),
                    DAO.flora.getRegionsToHealthcareRegions(),
                ]);

            children = getHealthcareRegions(
                healthcareRegions,
                regionsToHealthcareRegions,
                children,
            );
        }

        return {
            ...createOrganisation(userOrganistions, orgTree.root, "country"),
            children,
        };
    };

    useEffect(() => {
        const orgStructure = stat.orgStructures.find(
            (o) => o.value === "internal",
        );
        if (orgStructure == null || orgStructure.organisations.length === 0) {
            return;
        }
        const hasHealthcareRegion =
            stat.orgStructures
                .find((o) => o.value === "public")
                ?.dnStructure?.some((e) => e.value === "healthcareRegion") ??
            false;

        fetch(orgStructure, hasHealthcareRegion)
            .then((country) => {
                setCountry(country);
                setShowOrganisations(hasMultipleChildren(country));
            })

            .catch(addError);
    }, []);

    if (country == null) {
        return null;
    }

    const updateCurrentLevel = (selected: Organisation[], level?: number) => {
        if (selected.length === 0) {
            setCurrentLevel(100);
        } else if (level != null) {
            setCurrentLevel(level);
        }
    };

    const isSelected = (org: Organisation) => {
        return selected.includes(org);
    };

    const renderSelectedIcon = (org: Organisation) => {
        if (isSelected(org)) {
            return <Check />;
        }
        return <Placeholder />;
    };

    const select = (org: Organisation, level: number) => {
        let selectedNew = [...selected];
        let openedNew = opened;
        if (currentLevel !== 100 && level < currentLevel) {
            selectedNew = [];
            openedNew = undefined;
        }
        const index = selectedNew.indexOf(org);
        if (index === -1) {
            selectedNew.push(org);
        } else {
            selectedNew.splice(index, 1);
        }
        setSelected(selectedNew);
        setOpened(openedNew);
        updateCurrentLevel(selectedNew, level);
    };

    const openClose = (region: Organisation) => {
        if (opened === region) {
            setOpened(undefined);
        } else {
            setOpened(region);
        }
    };

    const remove = (org: Organisation) => {
        const selectedNew = [...selected];
        selectedNew.splice(selectedNew.indexOf(org), 1);
        setSelected(selectedNew);
        updateCurrentLevel(selectedNew);
    };

    const downloadReport = () => {
        const orgIds = selected.map((o) => o.id);
        setSelected([]);
        updateCurrentLevel([]);
        DAO.export
            .downloadReport(
                stat.measurement,
                stat.periods,
                orgIds,
                includeHistory,
                includeQuestions,
                filterParams,
            )
            .catch(addError);
    };

    const renderChildren = (org: Organisation, level: number) => {
        if (currentLevel < level) {
            return null;
        }
        if (org.type === "region") {
            return renderRegionBody(org, level);
        }
        return (
            <div>
                {org.children.map((child) => (
                    <div key={child.id}>
                        <span
                            className={classNames(
                                getIndentClass(level),
                                getOrgClass(child),
                            )}
                            onClick={() => select(child, level)}
                        >
                            {renderSelectedIcon(child)}
                            &nbsp;
                            {child.name}
                        </span>
                        {renderChildren(child, level + 1)}
                    </div>
                ))}
            </div>
        );
    };

    const renderRegionBody = (region: Organisation, level: number) => {
        if (currentLevel < level || !showOrganisations) {
            return null;
        }
        const result = [];
        if (opened !== region) {
            result.push(
                <div key={region.id}>
                    <span
                        onClick={() => openClose(region)}
                        className={classNames(
                            getIndentClass(level),
                            getOrgClass(),
                        )}
                    >
                        <Expand /> {t.showUnits}
                        &nbsp;
                        <BadgeLength>{region.children}</BadgeLength>
                    </span>
                </div>,
            );
        } else {
            result.push(
                <div key={region.id}>
                    <span
                        onClick={() => openClose(region)}
                        className={classNames(
                            getIndentClass(level),
                            getOrgClass(),
                        )}
                    >
                        <Collapse /> {t.hideUnits}
                        &nbsp;
                        <BadgeLength>{region.children}</BadgeLength>
                    </span>
                </div>,
                <div key={`${region.id}orgs`} className={getIndentClass(level)}>
                    {region.children.map((org) => (
                        <div key={org.id}>
                            <span
                                onClick={() => select(org, level)}
                                className={getOrgClass(org)}
                            >
                                {renderSelectedIcon(org)}
                                &nbsp;
                                {org.name}
                            </span>
                        </div>
                    ))}
                </div>,
            );
        }
        return result;
    };

    const title = t.downloadStandardReport;

    return (
        <section aria-label={title} className="download-report">
            <h2>
                {title}
                <br />
                <small>{t.standardReportDesc1}</small>
            </h2>
            <i>{t.standardReportDesc2}</i>

            <Row>
                <Col>
                    <span
                        className="clickable hover-bold"
                        onClick={() => select(country, 0)}
                    >
                        {renderSelectedIcon(country)}
                        &nbsp;
                        {country.name}
                    </span>
                    {renderChildren(country, 1)}
                </Col>

                <Col>
                    <InputCheckboxLabel
                        title={t.standardReportIncludeHistoryTooltip}
                        checked={includeHistory}
                        onChange={setIncludeHistory}
                    >
                        {t.standardReportIncludeHistory}
                    </InputCheckboxLabel>
                    <InputCheckboxLabel
                        checked={includeQuestions}
                        onChange={setIncludeQuestions}
                    >
                        {t.standardReportIncludeQuestions}
                    </InputCheckboxLabel>

                    <p />
                    {t.standardReportSelectedOrg}
                    <BadgeLength className="ms-1">{selected}</BadgeLength>

                    <p />
                    {selected.map((org) => (
                        <div key={org.id}>
                            <span
                                className="clickable hover-bold"
                                onClick={() => remove(org)}
                            >
                                <Remove /> {org.name}
                            </span>
                        </div>
                    ))}

                    <p />
                    <Button
                        variant="primary"
                        disabled={selected.length === 0}
                        onClick={downloadReport}
                    >
                        {t.downloadReport}
                    </Button>
                </Col>
            </Row>
        </section>
    );
}

function getHealthcareRegions(
    healthcareRegions: FloraOrg[],
    regionsToHealthcareRegions: StringRecord<string>,
    regions: Organisation[],
): Organisation[] {
    const healthcareRegionsMap = new Map(
        healthcareRegions.map((hr) => [hr.ids.gss, hr]),
    );
    const healthcareRegionsIndex = new Map(
        healthcareRegions.map((hr, index) => [hr.ids.gss, index]),
    );
    const resultsMap = new Map<string, Organisation>();

    for (const region of regions) {
        const healthcareRegionId = regionsToHealthcareRegions[region.id];
        if (healthcareRegionId == null) {
            throw new Error(
                `Healthcare region ID not found for region: ${region.id}`,
            );
        }
        const healthcareRegion = healthcareRegionsMap.get(healthcareRegionId);
        if (healthcareRegion == null) {
            throw new Error(
                `Healthcare region not found for ID: ${healthcareRegionId}`,
            );
        }
        if (healthcareRegion.name == null) {
            throw new Error(
                `Healthcare region name not found for ID: ${healthcareRegionId}`,
            );
        }

        let hrNode = resultsMap.get(healthcareRegionId);

        if (hrNode == null) {
            hrNode = {
                type: "healthcareRegion",
                id: healthcareRegionId,
                name: healthcareRegion.name,
                dn: `st=${healthcareRegion.name},c=Sverige`,
                prioritize: false,
                children: [],
            };
            resultsMap.set(healthcareRegionId, hrNode);
        }

        hrNode.children.push(region);
    }

    const result = Array.from(resultsMap.values());

    result.sort(
        (a, b) =>
            (healthcareRegionsIndex.get(a.id) ?? 0) -
            (healthcareRegionsIndex.get(b.id) ?? 0),
    );

    return result;
}

function getRegionsFromTree(
    orgTree: OrgTree<OrgStructureOrg, OrgStructureOrg>,
    userOrganistions: UserOrganisations,
): Organisation[] {
    const result: Organisation[] = [];

    for (const region of orgTree.root.children) {
        if (!region.dn.startsWith("o=")) {
            throw new Error(
                `Expected region organisation DN to start with "o=" for: ${region.dn}`,
            );
        }
        if (region.data == null) {
            throw new Error(`Expected data for region node: ${region.dn}`);
        }

        result.push({
            ...createOrganisation(userOrganistions, region, "region"),
            children: getOrganisations(userOrganistions, region),
        });
    }

    sortOrgs(result);

    return result;
}

function getOrganisations(
    userOrganistions: UserOrganisations,
    region: TreeNode<OrgStructureOrg>,
): Organisation[] {
    const updatedOrganisations = OrgTree.getLeafNodes(region).map((org) =>
        createOrganisation(userOrganistions, org, "unit"),
    );
    solveNameConflicts(updatedOrganisations);
    sortOrgs(updatedOrganisations);
    return updatedOrganisations;
}

function createOrganisation(
    userOrganistions: UserOrganisations,
    node: TreeNode<OrgStructureOrg>,
    type: Organisation["type"],
): Organisation {
    if (node.data == null) {
        throw new Error(`Expected data for node: ${node.dn}`);
    }
    return {
        type,
        id: node.data.id,
        name: node.name,
        dn: node.dn,
        prioritize: userOrganistions.manager.includes(node.data.id),
        children: [],
    };
}

function solveNameConflicts(organisations: Organisation[]): void {
    let conflicts = getNameConflicts(organisations);
    while (conflicts.length > 0) {
        for (const org of conflicts) {
            org.name = getLongName(org);
        }
        conflicts = getNameConflicts(conflicts);
    }
}

function getNameConflicts(organisations: Organisation[]): Organisation[] {
    const used: StringRecord<Organisation | true> = {};
    const conflicts: Organisation[] = [];
    for (const org of organisations) {
        const existing = used[org.name];
        if (existing == null) {
            used[org.name] = org;
        } else {
            if (existing !== true) {
                conflicts.push(existing);
                used[org.name] = true;
            }
            conflicts.push(org);
        }
    }
    return conflicts;
}

function getLongName(org: Organisation): string {
    const dn = new DN(org.dn);
    for (let i = dn.length() - 2; i > -1; --i) {
        const exp = dn.rdns
            .slice(i, dn.length() - 1)
            .map((r) => r.value)
            .join(", ");
        const newName = `${dn.getLastValue()} (${exp})`;
        if (newName.length > org.name.length) {
            return newName;
        }
    }
    console.error("Can't find long name for", org);
    return org.name;
}

function sortOrgs(list: { prioritize?: boolean; name: string }[]): void {
    list.sort(
        (a, b) =>
            // Prioritize organizations marked as 'prioritize' first
            Number(b.prioritize) - Number(a.prioritize) ||
            // Then sort alphabetically by name
            sweCompare(a.name, b.name),
    );
}

function getOrgClass(org?: { prioritize: boolean }): string | undefined {
    return classNames("clickable hover-bold", org?.prioritize && "fst-italic");
}

function getIndentClass(level: number) {
    switch (level) {
        case 1:
            return "download-report-1";
        case 2:
            return "download-report-2";
        case 3:
            return "download-report-3";
        default:
            throw new Error(`Unsupported indent level: ${level}`);
    }
}

function hasMultipleChildren(org: Organisation): boolean {
    return org.children.some(
        (r) => r.children.length > 1 || hasMultipleChildren(r),
    );
}
